import { logError } from '../utils/logError';
import { BOOL_FEATURE_FLAGS, FeatureFlagBool } from './FeatureFlagBool';
import { resolveBooleanFeatureFlag } from './override';
import { FeatureFlagOverrideStore } from './override/featureFlagOverrideTypes';
import { useFeatureFlagLocal } from './useFeatureFlagLocal';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
// eslint-disable-next-line no-restricted-imports
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

type Args = {
  serverFlags: LDFlagSet;
  overrideStore: FeatureFlagOverrideStore;
  flagKey: FeatureFlagBool;
};

/**
 * Resolves a boolean flag, preserving typed ML-only overrides for flags not yet created in
 * LaunchDarkly while keeping the ordinary missing-flag path safely disabled.
 */
export const getFeatureFlagBool = ({ serverFlags, overrideStore, flagKey }: Args): boolean => {
  const resolved = resolveBooleanFeatureFlag({ serverFlags, overrideStore, flagKey });
  if (!(flagKey in serverFlags) && !isEmpty(serverFlags) && resolved.source !== 'LOCAL_OVERRIDE') {
    logError(`Feature Flag "${flagKey}" is not being sent to client`);
    return false; // fail closed
  }

  return resolved.value;
};

/**
 * Boolean based feature flag checker.
 *
 * It complains if it can't find the specified flag in the LaunchDarkly payload.
 * This function also allows us to override flags so we can more easily UI test
 * different features with the same user account.
 *
 * https://docs.launchdarkly.com/home/getting-started/feature-flags
 */
export function useFeatureFlagBool(flagKey: FeatureFlagBool): boolean {
  const serverFlags = useFlags();
  const { overrideStore } = useFeatureFlagLocal();

  return getFeatureFlagBool({ serverFlags, overrideStore, flagKey });
}

/**
 * Returns all evaluated frontend feature flags.
 *
 * It complains if it can't find the specified flag in the LaunchDarkly payload.
 * This function also allows us to override flags so we can more easily UI test
 * different features with the same user account.
 *
 * https://docs.launchdarkly.com/home/getting-started/feature-flags
 */
export const useFeatureFlagBoolList = (): Record<FeatureFlagBool, boolean> => {
  const serverFlags = useFlags();
  const { overrideStore } = useFeatureFlagLocal();

  const list = useMemo(() => {
    const flagsList: Partial<Record<FeatureFlagBool, boolean>> = {};
    BOOL_FEATURE_FLAGS.forEach((flagKey) => {
      flagsList[flagKey] = getFeatureFlagBool({ serverFlags, overrideStore, flagKey });
    });
    return flagsList;
  }, [serverFlags, overrideStore]);

  // SAFETY: BOOL_FEATURE_FLAGS populates every FeatureFlagBool key before this memo returns.
  return list as Record<FeatureFlagBool, boolean>;
};
